import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PortalShell } from '@/components/portal/PortalShell'
import { PortalInput } from '@/components/portal/PortalInput'
import { useClientId } from '@/hooks/useClientId'
import { clientApi } from '@/lib/api-client'

const PBK_FORMS = [
  { no: 1, form: 'Formulir PBK. CDDS. 01', process: 'PROFIL PERUSAHAAN PIALANG BERJANGKA', type: 'COMPANY_PROFILE' },
  { no: 2, form: 'Formulir PBK. CDDS. 02', process: 'DOKUMEN PEMBERITAHUAN ADANYA RISIKO', type: 'RISK_DISCLOSURE' },
  { no: 3, form: 'Formulir PBK. CDDS. 03', process: 'PERATURAN PERDAGANGAN', type: 'TRADING_RULES' },
  { no: 4, form: 'Formulir PBK. CDDS. 04', process: 'APLIKASI PEMBUKAAN REKENING TRANSAKSI', type: 'AGREEMENT' },
  { no: 5, form: 'Formulir PBK. CDDS. 05', process: 'PERJANJIAN PEMBERIAN AMANAT', type: 'AGREEMENT' },
  { no: 6, form: 'Formulir PBK. CDDS. 06', process: 'PERNYATAAN TANGGUNG JAWAB ATAS KODE AKSES', type: 'PASSWORD_RESP' },
  { no: 7, form: 'Formulir PBK. CDDS. 07', process: 'PERNYATAAN BAHTS MELAKUKAN SIMULASI', type: 'AML' },
  { no: 8, form: 'Formulir PBK. CDDS. 08', process: 'PERNYATAAN PENGUNGKAPAN', type: 'DISPUTE_RESOLUTION' },
  { no: 9, form: 'Formulir PBK. CDDS. 09', process: 'PERNYATAAN KEPEMILIKAN DANA', type: 'AML' },
  { no: 10, form: 'Formulir PBK. CDDS. 10', process: 'VERIFIKASI KELENGKAPAN PROSES', type: 'COMPANY_PROFILE' },
  { no: 11, form: 'Formulir PBK. CDDS. 11', process: 'PERNYATAAN PENERIMAAN', type: 'AGREEMENT' },
] as const

export function PbkFormsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { clientId } = useClientId()
  const [accepted, setAccepted] = useState<'yes' | 'no'>('yes')
  const [declarationDate, setDeclarationDate] = useState(
    new Date().toISOString().slice(0, 10),
  )

  const { data: formStatus } = useQuery({
    queryKey: ['client', 'bappebti', clientId],
    queryFn: () => clientApi.getBappebtiForm(clientId!),
    enabled: Boolean(clientId),
  })

  const completedSet = new Set(formStatus?.consents_completed ?? [])

  return (
    <PortalShell>
      <div className="prod-page-content--center">
        <h1 className="prod-text-page-title prod-text-page-title--center">
          {t('pbk.tableTitle')}
        </h1>
        <p className="prod-auth__subtitle prod-auth__subtitle--center">
          {t('pbk.tableSubtitle')}
        </p>

        <div className="prod-pbk-table-wrap">
        <table className="prod-pbk-table prod-table--stack">
          <thead>
            <tr>
              <th>{t('pbk.colNo')}</th>
              <th>{t('pbk.colForm')}</th>
              <th>{t('pbk.colProcess')}</th>
              <th>{t('pbk.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {PBK_FORMS.map((row) => {
              const done = completedSet.has(row.type)
              return (
                <tr key={row.no}>
                  <td data-label={t('pbk.colNo')} className="prod-num">{row.no}</td>
                  <td data-label={t('pbk.colForm')} className="prod-cell-code">{row.form}</td>
                  <td data-label={t('pbk.colProcess')}>
                    {row.process}
                  </td>
                  <td data-label={t('pbk.colStatus')}>
                    <span className="prod-pbk-status-cell">
                      <span className="prod-pbk-status" aria-hidden="true">
                        {done ? <Check className="size-3" /> : null}
                      </span>
                      {done ? t('pbk.statusDone') : t('pbk.statusPending')}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>

        <p className="prod-auth__subtitle prod-pbk-declaration">
          {t('pbk.declaration')}
        </p>

        <div className="prod-grid-2">
          <div>
            <fieldset className="prod-field">
              <legend className="prod-field__label">{t('pbk.acceptLabel')}</legend>
              <div className="prod-radio-group">
                <label className="prod-radio">
                  <input
                    type="radio"
                    name="pbk-accept"
                    checked={accepted === 'no'}
                    onChange={() => setAccepted('no')}
                  />
                  {t('form.no')}
                </label>
                <label className="prod-radio">
                  <input
                    type="radio"
                    name="pbk-accept"
                    checked={accepted === 'yes'}
                    onChange={() => setAccepted('yes')}
                  />
                  {t('form.yes')}
                </label>
              </div>
            </fieldset>
          </div>
          <div>
            <p className="prod-field__label">{t('pbk.declarationDate')}</p>
            <PortalInput
              type="date"
              aria-label={t('pbk.declarationDate')}
              value={declarationDate}
              onChange={(e) => setDeclarationDate(e.target.value)}
            />
          </div>
        </div>

        <div className="prod-mt-lg">
          <button
            type="button"
            className="prod-btn prod-btn--gold"
            disabled={accepted !== 'yes'}
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ['client', 'status'] })
            }}
          >
            {t('form.submitForApproval')}
          </button>
        </div>
      </div>
    </PortalShell>
  )
}
